<template>
  <div class="lesson-preview-info">
    <match-indicator v-if="matchTitle && showInfoHeader" />
    <div v-if="showInfoHeader" class="info-header">
      <ellipsis-text class="lesson-title" :text="lesson.title" />
      <svg-icon
        v-if="sharingEnabled && showInfoHeader && !showInfoContent"
        name="SharingEnabled24"
        kind="indicative"
      />

      <cv-link class="lesson-link" @click="handleOpenLesson">
        <svg-icon class="view-ext" name="ExternalLink24" kind="action" />
      </cv-link>
    </div>

    <dl v-if="hasSubjectGroup && showInfoHeader && !showInfoContent" class="field-set group-name">
      <dd class="field-value">
        <label-component
          kind="class-group"
          :text="subjectGroupInfo.studentGroupName"
          :subject-key="subjectGroupInfo.subjectKey"
        />
      </dd>
    </dl>

    <cv-form v-if="showInfoContent" class="info-content">
      <dl v-if="showPasscodeEnabled" class="field-set graphic">
        <dd class="field-value">
          <PageNoAccess />
        </dd>
      </dl>
      <dl v-else-if="showThumbnail" class="field-set thumbnail">
        <dd class="field-value">
          <img :src="lesson.thumbnailUrl" alt="Thumbnail" />
        </dd>
      </dl>
      <match-indicator v-if="matchDescriptionOrNotes" />
      <dl v-if="showCover" class="field-set description">
        <dd class="field-value">
          <rich-text class="description" :content="lesson.description" />
        </dd>
      </dl>
      <dl v-if="showNotes" class="field-set notes">
        <dt class="field-label">
          <svg-icon name="ActivityNote24" kind="indicative" />
          <span>{{ t("search_resource_detail_resource_notes_label") }}</span>
        </dt>
        <dd class="field-value">
          <empty-placeholder v-if="isNotesEmpty">
            <template #icon>
              <AnnotationCardsEmpty />
            </template>
            {{ t("search_resource_detail_resource_notes_placeholder") }}
          </empty-placeholder>
          <rich-text class="notes" :content="lesson.notes" />
        </dd>
      </dl>
    </cv-form>
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from "vue";
import { useStore } from "vuex";
import { LocationQueryRaw, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { NAMESPACE } from "@modules/my-drive/my-drive-helper.ts";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant";
import { isEmpty } from "@utils/var.check";
import AnnotationCardsEmpty from "@images/placeholder/empty-states/AnnotationCardsEmpty.svg?component";
import PageNoAccess from "@images/placeholder/error-states/PageNoAccess.svg?component";
import { EditorRouteParams } from "@modules/editor/types.ts";
import MatchIndicator from "@plugins/common/components/MatchIndicator.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import LabelComponent from "@plugins/common/components/LabelComponent.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";

// 1. Props interface and declaration
interface Props {
  params: Record<string, unknown>;
  showNotes?: boolean;
  showInfoHeader?: boolean;
  showInfoContent?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  showNotes: false,
  showInfoHeader: true,
  showInfoContent: true,
});

// 2. Store getters
const store = useStore();
const isStudent = computed<boolean>(() => store.getters[`${NAMESPACE.AUTH}/isStudent`]);
const isTeacher = computed<boolean>(() => store.getters[`${NAMESPACE.AUTH}/isTeacher`]);
const isAssociatedAccount = computed<boolean>(
  () => store.getters[`${NAMESPACE.AUTH}/isAssociatedAccount`],
);

// 3. i18n
const { t } = useI18n();

// 4. Router
const router = useRouter();

// 5. Computed properties
const lesson = computed(() => props.params.lesson);
const isNotesEmpty = computed<boolean>(() => isEmpty(lesson.value.notes));
const showPasscodeEnabled = computed<boolean>(
  () => lesson.value.passcodeEnabled && isStudent.value,
);
const showCover = computed<boolean>(
  () => lesson.value.showCover || !isEmpty(props.params.highlights),
);
const showThumbnail = computed<boolean>(() => showCover.value && lesson.value.thumbnailUrl);
const showNotes = computed<boolean>(
  () => showCover.value && isTeacher.value && props.showNotes && props.params.showMoreInformation,
);

const matchDescriptionOrNotes = computed<boolean>(
  () =>
    (showCover.value && !isEmpty(props.params.highlights?.description)) ||
    (showNotes.value && !isEmpty(props.params.highlights?.notes)),
);
const matchTitle = computed<boolean>(
  () => !isEmpty(props.params.highlights) && !isEmpty(props.params.highlights.title),
);
const sharingEnabled = computed<boolean>(() => props.params.moduleData?.metaData?.sharingEnabled);
const hasSubjectGroup = computed<boolean>(
  () => !!props.params.moduleData?.metaData?.studentGroupName,
);
const subjectGroupInfo = computed(() => ({
  studentGroupName: props.params.moduleData?.metaData?.studentGroupName || "",
  subjectKey: props.params.moduleData?.metaData?.subjectKey || "",
}));
const isEAssessment = computed(() => props.params.moduleData?.metaData?.eAssessment);

// 6. Methods
function handleOpenLesson(): void {
  let routeName = "EditorCover";
  let resourceUuid = lesson.value.uuid;
  let resourceDomain = "";
  let routeParams: EditorRouteParams = {};
  const routeQuery: LocationQueryRaw = {};

  switch (props.params.lesson.resourceDomain) {
    case "PERSONAL":
      if (isStudent.value) routeName = "PersonalLessonAttemptCover";
      resourceDomain = isStudent.value
        ? RESOURCE_DOMAIN_PATH.PERSONAL_LESSON
        : RESOURCE_DOMAIN_PATH.PERSONAL;
      break;
    case "MOE":
      if (isStudent.value) routeName = "SelfStudyAttemptCover";
      resourceDomain = isStudent.value
        ? RESOURCE_DOMAIN_PATH.SELF_STUDY
        : RESOURCE_DOMAIN_PATH.MOE_LIBRARY;
      resourceUuid = lesson.value.moeLibraryUuid;
      routeQuery.version = lesson.value.uuid ? lesson.value.uuid : undefined;
      break;
    case "COMMUNITY":
    case "Community":
      resourceDomain = RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY;
      resourceUuid = props.params.communityResourceUuid;
      break;
    case "ALP_TEMPLATE":
      resourceDomain = RESOURCE_DOMAIN_PATH.ALP_TEMPLATE;
      resourceUuid = props.params.templateUuid;
      break;
    case "ASSIGNMENT":
      if (isStudent.value) routeName = "StudentGroupResourceAttemptCover";
      resourceDomain = RESOURCE_DOMAIN_PATH.CLASS_GROUP_RESOURCE;
      resourceUuid = props.params.lesson.uuid;
      break;
  }

  routeParams = {
    resourceDomain: resourceDomain,
    lessonUuid: resourceUuid,
    mode: "view",
  };

  // TODO: [SLS-11275] Visit MOE module in attempt mode
  if (
    props.params.isAssociatedStudentAccessibleOnlyMoeModule &&
    isTeacher.value &&
    isAssociatedAccount.value
  ) {
    routeName = "SelfStudyAttemptCover";
    routeParams = {
      lessonUuid: lesson.value.moeLibraryUuid,
    };
    routeQuery.version = lesson.value.uuid ? lesson.value.uuid : undefined;
  }

  if (props.params.type === "ASSIGNMENT_LESSON") {
    const assignmentUuid = props.params.assignmentUuid;
    if (isStudent.value) {
      routeName = isEAssessment.value ? "AssessmentAttemptCover" : "AssignmentAttemptCover";
    } else {
      routeName = isEAssessment.value ? "AssessmentViewCover" : "AssignmentViewCover";
    }
    // TODO: SLS-11043 (DONE), attempt
    routeParams = { assignmentUuid };
  }

  if (props.params.admin && props.params.admin === "admin") {
    routeParams.admin = "admin";
  }

  const routeData = router.resolve({
    name: routeName,
    params: routeParams,
    query: routeQuery,
  });
  window.open(routeData.href, "_blank");
}

// 7. Provide
provide("richTextContext", { lessonUuid: lesson.value?.uuid });
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.lesson-preview-info {
  .field-set {
    &.graphic {
      display: flex;
      justify-content: center;
    }

    &.notes {
      width: 100%;
      margin-top: $spacing-05;
      padding: $spacing-05;
      color: $text-01;
      background-color: $ui-01;
      border-radius: 10px;

      .field-label {
        @include carbon--type-style("productive-heading-01");
        display: flex;
        align-items: center;

        .sls-icon {
          margin-right: $spacing-03;
        }
      }

      .field-value {
        @include carbon--type-style("body-long-01");
      }

      .empty-placeholder {
        margin: 0;

        .content {
          margin-bottom: 0;
        }

        .placeholder-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 80px;
          margin: 0;
        }

        svg {
          transform: scale(0.5);
        }
      }
    }
  }

  .thumbnail {
    img {
      width: 100%;
      height: 100%;
    }
  }

  .info-header {
    display: flex;
    align-items: center;
  }
  .lesson-title {
    @include carbon--type-style("productive-heading-02", true);
    @include line-clamp(2);
  }
  .lesson-link {
    display: inline-flex;
    margin-left: $spacing-03;

    .sls-icon {
      [fill] {
        fill: $interactive-01;
      }
    }
  }
}
</style>
